import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ArrowLeft, Coins, Crown, Truck, Clock, Storefront, Info, Spinner, Star, Package,
} from "@phosphor-icons/react";

const money = (v) => `$${Number(Math.round(v || 0)).toLocaleString()}`;
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function Profitability() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [listings, setListings] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/listings")
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : (data.items || []);
        const priced = arr.filter((l) => l.price_retail != null && Number(l.price_retail) > 0);
        setListings(priced);
        const initial = params.get("listing") || priced[0]?.id || "";
        if (initial) selectListing(initial, priced);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const selectListing = (id, source) => {
    const list = source || listings;
    const l = list.find((x) => x.id === id);
    setSelectedId(id);
    if (l) setPriceInput(String(Math.round(Number(l.price_retail) || 0)));
  };

  // Refetch (debounced) whenever the piece or the modeled price changes.
  useEffect(() => {
    if (!selectedId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      const p = priceInput !== "" ? `?price=${encodeURIComponent(priceInput)}` : "";
      api.get(`/listings/${selectedId}/profitability${p}`)
        .then(({ data }) => { setData(data); setLocked(false); })
        .catch((e) => {
          if (e.response?.status === 403) { setLocked(true); setData(null); }
          else setData(null);
        })
        .finally(() => setLoading(false));
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [selectedId, priceInput]);

  const listingOpts = useMemo(
    () => listings.slice(0, 300).map((l) => ({
      id: l.id,
      label: `${(l.listing_headline || l.title || "Untitled").slice(0, 52)} · ${money(l.price_retail)}`,
    })),
    [listings]
  );

  const rows = data?.rows || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="profitability-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage" onClick={() => navigate("/dashboard")} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#E5E0D6] cursor-pointer shrink-0 hover:ring-[#B47A3C] transition-all" data-testid="app-compass-mark" title="Vintage Vantage — home" />
            <button onClick={() => navigate("/dashboard")} data-testid="prof-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
              <ArrowLeft size={18} weight="bold" /> Dashboard
            </button>
          </div>
          <span className="text-sm font-semibold text-[#525252] truncate max-w-[200px]">{user?.name || user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Coins size={22} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">Marketplace intelligence · Profitability</span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0D1B2A] text-[#FBBF24]">
            <Crown size={11} weight="fill" /> Founder beta
          </span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">
          Net by marketplace
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-8">
          Pick a piece and see what you'd actually keep on each curated marketplace, side by side — list price
          minus each platform's commission. The bird's-eye view no single site shows you.
        </p>

        {locked ? (
          <div className="border border-[#0D1B2A] rounded-xl p-8 text-center bg-white max-w-xl" data-testid="prof-locked">
            <Crown size={34} weight="fill" className="mx-auto text-[#D97706] mb-3" />
            <h2 className="text-xl font-bold mb-2">Net-by-marketplace is in Founder beta</h2>
            <p className="text-[#525252] mb-5">
              We're testing the profitability view with our Founding Council first. Grab a founding spot to get
              early access — and lock in the founding rate for life.
            </p>
            <button onClick={() => navigate("/founding")} data-testid="prof-unlock-button"
              className="inline-flex items-center gap-2 h-11 px-5 bg-[#0D1B2A] text-[#FDFBF7] rounded-md font-semibold hover:bg-black transition-colors">
              <Crown size={16} weight="fill" /> Join the Founding Council
            </button>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="border border-[#0D1B2A] bg-white rounded-xl p-5 sm:p-6 mb-6" data-testid="prof-controls">
              <div className="grid sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="label-caps block mb-1.5">Pick a piece</label>
                  {listingOpts.length ? (
                    <Select value={selectedId} onValueChange={(v) => selectListing(v)}>
                      <SelectTrigger className="h-11 text-base" data-testid="prof-listing-select">
                        <SelectValue placeholder="Choose one of your priced pieces…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {listingOpts.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-[#8A867D]">No priced pieces yet — add a price on a listing to compare.</p>
                  )}
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Model a list price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A867D] font-semibold">$</span>
                    <input type="number" step="any" value={priceInput} onChange={(e) => setPriceInput(e.target.value)}
                      data-testid="prof-price-input"
                      className="w-full h-11 pl-7 pr-3 rounded-md border border-[#D6D3CD] bg-white text-base focus:outline-none focus:border-[#0D1B2A]" />
                  </div>
                  <p className="text-xs text-[#8A867D] mt-1">Try different prices — the whole table recalculates.</p>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="border border-[#D6D3CD] bg-[#F5F3ED] p-6 rounded-xl inline-flex items-center gap-2 text-[#57534E]" data-testid="prof-loading">
                <Spinner size={18} className="animate-spin" /> <span className="text-sm font-medium">Crunching the numbers…</span>
              </div>
            ) : data?.needs_price ? (
              <div className="border border-dashed border-[#D6D3CD] rounded-xl p-8 text-center bg-[#FBFAF6]" data-testid="prof-needs-price">
                <Package size={32} weight="light" className="mx-auto text-[#B8B2A6] mb-3" />
                <p className="text-[#8A867D]">Enter a list price above to see the net comparison.</p>
              </div>
            ) : rows.length ? (
              <>
                <p className="text-sm text-[#57534E] mb-3">
                  If you list <strong className="text-[#0D1B2A]">{data.listing.headline}</strong> at{" "}
                  <strong className="text-[#0D1B2A]">{money(data.listing.price)}</strong>, here's your estimated net:
                </p>

                <div className="overflow-x-auto border border-[#0D1B2A] rounded-xl bg-white" data-testid="prof-table">
                  <table className="w-full text-left border-collapse min-w-[720px]">
                    <thead>
                      <tr className="bg-[#0D1B2A] text-[#FDFBF7] text-xs uppercase tracking-wide">
                        <th className="px-4 py-3 font-semibold">Marketplace</th>
                        <th className="px-4 py-3 font-semibold">Commission</th>
                        <th className="px-4 py-3 font-semibold">Estimated net</th>
                        <th className="px-4 py-3 font-semibold">You keep</th>
                        <th className="px-4 py-3 font-semibold">Who ships</th>
                        <th className="px-4 py-3 font-semibold">Payout</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.platform} data-testid={`prof-row-${slug(r.platform)}`}
                          className={`border-t border-[#EAE6DD] align-top ${r.best ? "bg-[#F0FDF4]" : ""}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 font-semibold text-[#0D1B2A]">
                              <Storefront size={15} weight="fill" className="text-[#B47A3C]" /> {r.platform}
                            </div>
                            {r.best ? (
                              <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#166534] text-white" data-testid="prof-best-badge">
                                <Star size={10} weight="fill" /> Best net
                              </span>
                            ) : null}
                            {r.monthly_fee ? <p className="text-[11px] text-[#8A867D] mt-1">+ ${r.monthly_fee}/mo shop fee</p> : null}
                            {r.min_fee ? <p className="text-[11px] text-[#8A867D] mt-1">min fee {money(r.min_fee)}</p> : null}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-[#0D1B2A]">
                              {r.commission_low === r.commission_high ? `~${r.commission_low}%` : `${r.commission_low}–${r.commission_high}%`}
                            </span>
                            <p className="text-[11px] text-[#8A867D] leading-snug mt-0.5 max-w-[220px]">{r.commission_note}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-lg font-extrabold text-[#0D1B2A]">
                              {r.net_low === r.net_high ? money(r.net_high) : `${money(r.net_low)}–${money(r.net_high)}`}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#57534E]">
                            {r.net_pct_low === r.net_pct_high ? `${r.net_pct_high}%` : `${r.net_pct_low}–${r.net_pct_high}%`}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${r.ships_for_you ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                              <Truck size={12} weight="fill" /> {r.ships_for_you ? "They handle it" : "You arrange it"}
                            </span>
                            <p className="text-[11px] text-[#8A867D] leading-snug mt-1 max-w-[200px]">{r.logistics}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs text-[#57534E]">
                              <Clock size={12} /> ~{r.payout_days}d
                            </span>
                            <p className="text-[11px] text-[#8A867D] leading-snug mt-1 max-w-[190px]">{r.payout}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-[11px] text-[#8A867D] mt-3 flex items-start gap-1.5 max-w-3xl" data-testid="prof-note">
                  <Info size={13} className="mt-0.5 shrink-0" /> {data.note}
                </p>
              </>
            ) : (
              <div className="border border-dashed border-[#D6D3CD] rounded-xl p-8 text-center bg-[#FBFAF6]" data-testid="prof-empty">
                <Coins size={32} weight="light" className="mx-auto text-[#B8B2A6] mb-3" />
                <p className="text-[#8A867D]">Pick a priced piece above to compare your net across marketplaces.</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
